import { useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import classNames from 'classnames';
import _ from 'lodash';
import queryString from 'query-string';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import LoadingIndicator from '../components/LoadingIndicator';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import { parseSearchQuery } from '../topbar/quillFacets';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import { useLocation, useRouter } from '../utils/router/v3Bridge';
import SearchManagePlatformButton from './SearchManagePlatformButton';
import styles from './SearchView.module.scss';
import SearchViewExternalResults from './SearchViewExternalResults';
import SearchViewFilterMenu from './SearchViewFilterMenu';
import SearchViewHeader from './SearchViewHeader';
import SearchViewSlabResults from './SearchViewSlabResults';
import getSearchResultType, {
  filterBySearchResultType,
} from './getSearchResultType';

const ENCOURAGE_INSTALLATION_PLATFORMS = ['google-drive', 'slack'];

const PREVIEW_SLAB_RESULT_COUNT = 10;
const FULL_SLAB_RESULT_COUNT = 100;
const SLAB_RESULT_PAGE_COUNT = 25;

const SLAB_SEARCH_QUERY = gql(`
  query SearchViewSlabSearch(
    $query: String!
    $ancestorIds: [ID]
    $ownerId: ID
    $contributorIds: [ID]
    $byIds: [ID]
    $mentionIds: [ID]
    $isStates: [SearchIsState]
    $first: Int!
    $afterCursor: String
    $types: [String!]
    $timestamp: TimestampFilter
  ) {
    search(
      query: $query
      ancestorIds: $ancestorIds
      ownerId: $ownerId
      contributorIds: $contributorIds
      byIds: $byIds
      mentionIds: $mentionIds
      isStates: $isStates
      first: $first
      after: $afterCursor
      types: $types
      timestamp: $timestamp
    ) {
      edges {
        node {
          ...SearchViewSlabResults_results
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      queryId
    }
  }
`);

const useSlabSearchQuery = ({
  variables,
  skip,
  loadPreview,
}: {
  variables: Omit<VariablesOf<typeof SLAB_SEARCH_QUERY>, 'first'>;
  skip?: boolean;
  loadPreview?: boolean;
}) => {
  const previewResponse = useQuery(SLAB_SEARCH_QUERY, {
    fetchPolicy: 'network-only',
    skip: skip || !loadPreview,
    variables: {
      ...variables,
      first: PREVIEW_SLAB_RESULT_COUNT,
    },
  });

  const fullResponse = useQuery(SLAB_SEARCH_QUERY, {
    fetchPolicy: 'network-only',
    skip,
    variables: { ...variables, first: FULL_SLAB_RESULT_COUNT },
  });

  const [pageNumber, setPageNumber] = useState(0);
  const fetchMoreQuery = ConnectionHelper.generateFetchMore(
    SLAB_SEARCH_QUERY,
    fullResponse,
    'search',
    {
      afterCursorOffset:
        FULL_SLAB_RESULT_COUNT + SLAB_RESULT_PAGE_COUNT * pageNumber,
    },
  );
  const fetchMore: typeof fetchMoreQuery =
    fetchMoreQuery &&
    (async (...args) => {
      const results = await fetchMoreQuery(...args);
      setPageNumber((currentPageNumber) => currentPageNumber + 1);
      return results;
    });

  const fullResults = useMemo(
    () =>
      !fullResponse.loading && fullResponse.data
        ? _.compact(fullResponse.data?.search?.edges?.map((edge) => edge?.node))
        : undefined,
    [fullResponse.loading, fullResponse.data],
  );

  const previewResults = useMemo(
    () =>
      !previewResponse.loading
        ? _.compact(
            previewResponse.data?.search?.edges?.map((edge) => edge?.node),
          )
        : [],
    [previewResponse.loading, previewResponse.data?.search?.edges],
  );

  return {
    data: fullResults ?? previewResults,
    hasMoreResults: !!fullResponse?.data?.search?.pageInfo.hasNextPage,
    loading: fullResponse.loading,
    previewLoading: previewResponse.loading,
    fetchMore:
      fetchMore && (() => fetchMore({ first: SLAB_RESULT_PAGE_COUNT })),
    queryId:
      fullResponse.data?.search?.queryId ??
      previewResponse.data?.search?.queryId ??
      undefined,
  };
};

const EXTERNAL_SEARCH_QUERY = gql(`
  query SearchViewExternalResults($platform: String!, $query: String!) {
    externalSearch(platform: $platform, query: $query) {
      id
      ...SearchViewExternalResults_mention
    }
  }
`);

const USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT = gql(`
  fragment useExternalSearchQuery_platform on ExternalSearchPlatform {
    platform
    personalAuthorized
    teamAuthorized
  }
`);

const useExternalSearchQuery = (
  searchQuery: string,
  platform?: DocumentType<typeof USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT>,
  type?: string,
) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    personalAuthorized,
    platform: platformKey = '',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    teamAuthorized,
  } = platform ?? {};

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const isAuthorized = personalAuthorized || teamAuthorized;

  const { data, error, loading } = useQuery(EXTERNAL_SEARCH_QUERY, {
    skip: !platform || !platformInfo || !isAuthorized,
    variables: { platform: platformKey, query: searchQuery },
  });

  const results = data?.externalSearch ?? [];
  const filteredResults = _.compact(filterBySearchResultType(type, results));

  return { results, filteredResults, error, loading };
};

const EXTERNAL_PLATFORMS_QUERY = gql(`
  query SearchViewExternalPlatforms($redirectPath: String!) {
    externalSearchPlatforms(redirectPath: $redirectPath) {
      ...useExternalSearchQuery_platform
      ...SearchManagePlatformButton_platforms
      ...SearchViewHeader_platforms
      ...SearchViewExternalResults_platform
    }
  }
`);

const useExternalSearchPlatforms = () => {
  const userType = useAppSelector((state) => state.session.user.type);

  const location = useLocation();
  const redirectSearch = { q: location.query.q, platform: '$PLATFORM' };
  const searchString = `?${queryString.stringify(redirectSearch)}`;
  const redirectPath = `${location.pathname}${searchString}`;

  const { data, loading, refetch } = useQuery(EXTERNAL_PLATFORMS_QUERY, {
    fetchPolicy: 'network-only',
    variables: { redirectPath },
  });

  const externalSearchPlatforms = data?.externalSearchPlatforms;

  return useMemo(() => {
    const results = externalSearchPlatforms?.map((platform) => ({
      ...platform,
      teamAuthorized: userType !== 'GUEST' ? platform.teamAuthorized : false,
    }));

    return { loading, data: results, refetch };
  }, [externalSearchPlatforms, loading, refetch, userType]);
};

const useEncourageInstallationSet = (
  platforms?: { personalAuthorized?: boolean; teamAuthorized?: boolean }[],
) => {
  const dismissedSearchConnectionPrompts = useAppSelector(
    (state) => state.ui.dismissedSearchConnectionPrompts,
  );

  return useMemo(() => {
    if (
      !platforms ||
      platforms.some((p) => p.personalAuthorized || p.teamAuthorized)
    ) {
      return new Set([]);
    }

    return new Set(
      _.without(
        ENCOURAGE_INSTALLATION_PLATFORMS,
        ...dismissedSearchConnectionPrompts,
      ),
    );
  }, [dismissedSearchConnectionPrompts, platforms]);
};

const SearchView = () => {
  const router = useRouter();
  const isDesktop = useIsSize(Sizes.LG);
  const location = useLocation();

  const {
    authError,
    platform: userPlatformKey,
    q: searchInput = '',
    type,
  } = location.query as {
    authError?: string;
    platform?: string;
    q?: string;
    type?: string;
  };

  const platformKey =
    userPlatformKey && SEARCH_PLATFORM_INFO_MAP[userPlatformKey]?.platform;

  const parsedSearchInput = parseSearchQuery(searchInput);

  useLayoutEffect(() => {
    if (userPlatformKey && !platformKey) {
      router.push({
        ...location,
        query: { ...location.query, platform: undefined },
      });
    }
  }, [location, platformKey, router, userPlatformKey]);

  const slabAllResponse = useSlabSearchQuery({
    variables: parsedSearchInput,
    loadPreview: true,
  });

  const slabFilteredResponse = useSlabSearchQuery({
    variables: { ...parsedSearchInput, types: type ? [type] : [] },
    skip: !!platformKey || !type,
  });

  const slabCountsByType = useMemo(() => {
    if (slabAllResponse.loading) {
      return {};
    }

    return _.mergeWith(
      _.countBy(slabAllResponse.data, getSearchResultType),
      type && slabFilteredResponse.data?.length
        ? { [type]: slabFilteredResponse.data.length }
        : {},
      (a = 0, b = 0) => Math.max(a, b),
    );
  }, [
    slabAllResponse.data,
    slabAllResponse.loading,
    slabFilteredResponse.data.length,
    type,
  ]);

  const { fetchMore, loading, data, queryId } = type
    ? slabFilteredResponse
    : slabAllResponse;

  const { data: platforms, refetch: refetchPlatforms } =
    useExternalSearchPlatforms();
  const platform = platforms?.find((p) => p.platform === platformKey);
  const encouragedPlatformSet = useEncourageInstallationSet(platforms);

  const externalResponse = useExternalSearchQuery(
    parsedSearchInput.query,
    platform,
    type,
  );

  const isSearching =
    slabAllResponse.previewLoading ||
    slabAllResponse.loading ||
    slabFilteredResponse.loading ||
    externalResponse.loading;

  if (!platformKey && !slabAllResponse) {
    return null;
  }

  const slabAllResultCount = slabAllResponse.loading
    ? null
    : `${slabAllResponse.data.length}${slabAllResponse.fetchMore ? '+' : ''}`;
  const externalCountsByType = _.countBy(
    externalResponse.results,
    getSearchResultType,
  );

  return (
    <div id="content" className="no-padding-x">
      <div
        className={classNames(styles.container, {
          [styles.showSidebar]: isDesktop,
        })}
      >
        <div className={classNames(styles.title, styles.headerContainer)}>
          <div className={styles.headerTitle}>
            <h2>Search results</h2>
            <div
              className={classNames(styles.loading, {
                [styles.loadingHidden]: !isSearching,
              })}
            >
              <LoadingIndicator delay={0} adaptColor />
            </div>
          </div>
          {isDesktop ? (
            <SearchViewFilterMenu />
          ) : (
            <SearchManagePlatformButton
              platforms={platforms || []}
              refetch={refetchPlatforms}
            />
          )}
        </div>
        <div className={styles.header}>
          <SearchViewHeader
            activePlatformKey={platformKey}
            encouragedPlatformSet={encouragedPlatformSet}
            platforms={platforms || []}
            searchQuery={parsedSearchInput.query}
            type={type}
            externalCountsByType={externalCountsByType}
            vertical={isDesktop}
            slabAllResultCount={slabAllResultCount}
            slabCountsByType={slabCountsByType}
            verticalExtra={
              isDesktop ? (
                <div className={styles.verticalExtraContainer}>
                  <SearchManagePlatformButton
                    platforms={platforms || []}
                    refetch={refetchPlatforms}
                  />
                </div>
              ) : null
            }
          />
        </div>
        <div className={styles.results}>
          {platformKey ? (
            platform && (
              <SearchViewExternalResults
                authError={authError}
                encouragedPlatformSet={encouragedPlatformSet}
                platform={platform}
                results={externalResponse.filteredResults}
                loading={externalResponse.loading}
                error={externalResponse.error}
              />
            )
          ) : (
            <SearchViewSlabResults
              data={data}
              loading={loading}
              fetchMore={fetchMore}
              queryId={queryId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchView;
